// Pure builder for a deliverable's `params` object from the raw string values
// of the per-content-type parameters editor. Kept out of the component so the
// coercion and omission rules are machine-verifiable
// (web/test/deliverableParams.test.js).
//
// The dispatcher reads `deliverable.params.width` and friends directly, so an
// empty field must omit its key entirely rather than send `""` or `undefined` -
// a present-but-empty value would be worse than absent.

import type { ContentType } from "./types.js";

/** Raw editor state: one string per field, keyed by the param name. */
export type DeliverableParamForm = Record<string, string>;

/** The fields the editor renders per content type, plus `format` for any type. */
export const PARAM_FIELDS: Record<ContentType, Array<{ key: string; kind: "number" | "text" }>> = {
  text: [],
  svg: [],
  image: [
    { key: "width", kind: "number" },
    { key: "height", kind: "number" },
  ],
  audio: [{ key: "voice", kind: "text" }],
  video: [{ key: "durationSec", kind: "number" }],
};

function fieldsFor(contentType: ContentType): Array<{ key: string; kind: "number" | "text" }> {
  return [...(PARAM_FIELDS[contentType] ?? []), { key: "format", kind: "text" }];
}

/**
 * Turn the editor's string form into the `params` object to persist. Empty
 * inputs are dropped; numeric strings are coerced to numbers. Throws
 * `RangeError` on a negative number (a negative width/height/duration is never
 * meaningful and should surface to the user, not be silently written).
 */
export function buildParams(
  contentType: ContentType,
  form: DeliverableParamForm,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const { key, kind } of fieldsFor(contentType)) {
    const raw = (form[key] ?? "").trim();
    if (raw === "") continue;
    if (kind === "number") {
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      if (n < 0) throw new RangeError(`${key} must not be negative.`);
      out[key] = n;
    } else {
      out[key] = raw;
    }
  }
  return out;
}

/** Seed the editor's string form from a stored `params` object. */
export function formFromParams(
  contentType: ContentType,
  params: Record<string, unknown>,
): DeliverableParamForm {
  const form: DeliverableParamForm = {};
  for (const { key } of fieldsFor(contentType)) {
    const v = params[key];
    form[key] = v === undefined || v === null ? "" : String(v);
  }
  return form;
}
